import { UzButton, UzTabs } from '@utazzo/ui';
import { FileSpreadsheet, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { TransfersMediaManager } from '../components/TransfersMediaManager';

import { TransfersListPage, ViewToggle, type View } from './TransfersListPage';

import { PageShell } from '@/components/page-shell';
import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

type Tab = 'listing' | 'media';
const isTab = (v: string | null): v is Tab => v === 'listing' || v === 'media';

/**
 * Transfers inbox - the home of the transfers module.
 *
 * Wraps two tabs (mirrors the Lead inbox pattern):
 *   - **Listing** - the full DataGrid of transfers, re-uses `TransfersListPage`
 *     in embedded mode so the page header (title + actions) is owned here.
 *   - **Media** - placeholder for the upcoming media view.
 *
 * Tab state is URL-driven (`?tab=listing|media`) so operators can share a
 * direct link.
 */
export const TransfersInboxPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const writeParams = useUrlParamWriter();
  const raw = params.get('tab');
  const tab: Tab = isTab(raw) ? raw : 'listing';
  // View state lives here so the ViewToggle can sit in the action bar next
  // to Bulk import + Add (mirrors the hotels list layout).
  const [view, setView] = useState<View>('list');

  // Merges into the LIVE URL - the listing tab's grid state (rows-per-page,
  // filters, sort) is written by nuqs, which React Router's params snapshot
  // never sees. Rebuilding from that snapshot would drop it on every switch.
  const setTab = (next: Tab): void => {
    writeParams((p) => p.set('tab', next));
  };

  return (
    <PageShell
      title="Transfers"
      description="Point-to-point transport. Per-vehicle pricing, capacity-first. Surfaced in itinerary Transfer pickers."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {tab === 'listing' && <ViewToggle view={view} onChange={setView} />}
          <UzButton variant="outline" size="sm" onClick={() => navigate('/transfers/imports')}>
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />
            Bulk import
          </UzButton>
          <UzButton size="sm" onClick={() => navigate('/transfers/new')}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add transfer
          </UzButton>
        </div>
      }
    >
      <UzTabs
        value={tab}
        onValueChange={(v) => setTab(v as Tab)}
        tabs={[
          {
            value: 'listing',
            label: 'Listing',
            content: <TransfersListPage embedded view={view} onViewChange={setView} />,
          },
          {
            value: 'media',
            label: 'Media',
            content: <TransfersMediaManager />,
          },
        ]}
      />
    </PageShell>
  );
};
